import { CertificationSimulationsCertification } from './subcomponents/CertificationSimulationsCertification';
import { CertificationSimulationsPractice } from './subcomponents/CertificationSimulationsPractice';
import { CertificationSimulationsRoleplays } from './subcomponents/CertificationSimulationsRoleplays';
import { hasAnyPassingAttempt } from '@/domains/scoring';
import { ICertification, ISimulation, ISimulationScore } from '@/types';
import { RoleplayCardProps } from '@/components/Cards';
import { SIMULATION_LABELS } from '@/constants';
import { TextHeadline } from '@/components/Text';
import { useSimulationRouting } from '@/hooks';
import { useGlobalStore } from '@/store';
import React, { useMemo } from 'react';

export interface CertificationSimulationsProps {
  certification: ICertification;
  certificationPractice: ICertification['certificationPractice'];
  certificationSimulation: ICertification['certificationSimulation'];
  isLoading?: boolean;
  onShareWithManager: RoleplayCardProps['onShareWithManager'];
  onViewScoreCard: RoleplayCardProps['onViewScoreCard'];
  showFailBorder?: boolean;
  simulations: ICertification['simulations'];
  userId?: string;
  uuid: ICertification['uuid'];
}

export const CertificationSimulations: React.FC<CertificationSimulationsProps> = ({
  certification,
  certificationPractice,
  certificationSimulation,
  isLoading = false,
  onShareWithManager,
  onViewScoreCard,
  showFailBorder = false,
  simulations,
  userId,
  uuid,
}) => {
  // ==============================
  // Global store and hooks
  // ==============================
  const { personas, setActiveCertificate, scores, setSidebarCertificate } = useGlobalStore();
  const { navigateToSimulation } = useSimulationRouting();

  const createSimulationNavigationHandler = (simulation: ISimulation) => {
    return (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.preventDefault();
      event?.stopPropagation();
      return navigateToSimulation(certification, simulation);
    };
  };

  // ==============================
  // Derived values
  // ==============================
  const hasCertification = certification !== undefined;
  const hasCertificationSimulation = certificationSimulation !== undefined;
  const hasPracticeSimulation = certificationPractice !== undefined;
  const showPracticeOrFinalSimulation = hasCertificationSimulation || hasPracticeSimulation;

  /**
   * Determines if the certification has been passed based on the
   * certification simulation status and certPass flag.
   */
  const certificationPassed = useMemo(() => {
    // If there is no certification or no certification simulation, we consider it not passed
    if (!hasCertification || !hasCertificationSimulation) return false;

    // If the certPass flag is true, the certification is passed regardless of the simulation status
    if (certification?.certPass) return true;

    // If the status of the certification simulation is completed, we consider the certification passed
    if (certificationSimulation?.status === 'completed') return true;

    // In all other cases, we consider the certification not passed
    return false;
  }, [hasCertification, hasCertificationSimulation, certification, certificationSimulation]);

  /**
   * Determine if the roleplay card should show as failed (with red border).
   */
  const getRedFailBorder = (sim: ISimulation, scores: ISimulationScore[]): string => {
    if (!showFailBorder) return '';
    const redClassnames = 'border-red-accent-500 ring-2 ring-inset ring-accent-red-500';
    const hasHistory = sim?.history?.length !== 0;
    const simScores = scores.filter((s) => s.simulationId === sim.id.toString());
    const hasAtLeastOnePass = hasAnyPassingAttempt(simScores, sim.type);
    const showRedBorder = hasHistory && simScores.length > 0 && !hasAtLeastOnePass;

    return showRedBorder ? redClassnames : '';
  };

  /**
   * Handle click on the final certification simulation:
   * - If the certification simulation exists and is not completed, navigate to the simulation.
   * - If the certification simulation is completed, set the active certificate.
   */
  const handleFinalCertificationClick = () => {
    if (!certificationSimulation) return;
    if (!certificationPassed) {
      navigateToSimulation(certification, certificationSimulation);
    } else {
      setActiveCertificate(certification);
      setSidebarCertificate({
        active: true,
        certification,
        score: null,
        simulation: null,
        title: null,
        userName: null,
      });
    }
  };

  return (
    <div
      data-content="Certification Simulations"
      data-testid={`certification-${uuid}-simulations`}
      id={`certification-simulations-${uuid}`}
    >
      {/* Roleplay Simulations */}
      <CertificationSimulationsRoleplays
        isLoading={isLoading}
        onClick={createSimulationNavigationHandler}
        onShareWithManager={onShareWithManager}
        onViewScoreCard={onViewScoreCard}
        personas={personas}
        scores={scores}
        simulations={simulations}
        userId={userId}
      />

      {/* Certification Simulation(s) */}
      {showPracticeOrFinalSimulation && (
        <div className="bg-neutral-gray-200 p-6">
          <TextHeadline
            className="mb-4"
            color="neutral-black"
            data-testid="certification-simulations-header"
            level={3}
            text={SIMULATION_LABELS.CERTIFICATION_SCENARIOS}
            variant="sectionSubTitle"
          />

          {/* Practice Simulation */}
          {certificationPractice && (
            <CertificationSimulationsPractice
              certificationPractice={certificationPractice}
              getRedFailBorder={getRedFailBorder}
              isLoading={isLoading}
              onClick={createSimulationNavigationHandler}
              onShareWithManager={onShareWithManager}
              onViewScoreCard={onViewScoreCard}
              personas={personas}
              scores={scores}
              userId={userId}
            />
          )}

          {/* Final Certification Simulation */}
          {certificationSimulation && (
            <CertificationSimulationsCertification
              certificationPassed={certificationPassed}
              certificationSimulation={certificationSimulation}
              getRedFailBorder={getRedFailBorder}
              isLoading={isLoading}
              onShareWithManager={onShareWithManager}
              onStartClick={createSimulationNavigationHandler}
              onViewCertificateClick={handleFinalCertificationClick}
              onViewScoreCard={onViewScoreCard}
              personas={personas}
              scores={scores}
              userId={userId}
            />
          )}
        </div>
      )}
    </div>
  );
};
